"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ChevronLeft, ArrowRight } from "lucide-react";
import Link from "next/link";

type Convo = {
  label: { en: string; es: string };
  contact: string;
  status: string;
  bubble: { en: string; es: string };
  reply?: { en: string; es: string };
  time: string;
};

const CONVOS: Convo[] = [
  {
    label: { en: "Customer · Order ready", es: "Cliente · Orden lista" },
    contact: "Pepitos",
    status: "iMessage",
    bubble: {
      en: "Hi Maria — your Birria Plate is ready for pickup. We saved you a seat at the counter. — Pepitos",
      es: "Hola Maria — tu Birria Plate está lista para pickup. Te guardamos un lugar en el counter. — Pepitos",
    },
    reply: { en: "On my way!", es: "¡Voy!" },
    time: "11:42 AM",
  },
  {
    label: { en: "Staff · Schedule published", es: "Staff · Horario publicado" },
    contact: "Pepitos · Schedule",
    status: "Text Message",
    bubble: {
      en: "Hi James! Your schedule for the week of Oct 20: Fri 4:00PM-11:00PM, Sat 4:00PM-11:00PM — Pepitos",
      es: "¡Hola James! Tu horario semana del Oct 20: Vie 4:00PM-11:00PM, Sab 4:00PM-11:00PM — Pepitos",
    },
    time: "9:14 AM",
  },
  {
    label: { en: "Owner · Low-stock alert", es: "Dueño · Stock bajo" },
    contact: "Redy · Inventory",
    status: "Text Message",
    bubble: {
      en: "Redy: low stock at Pepitos: • Tortillas: 12 pcs (threshold: 40 pcs) • Avocados: 6 pcs (threshold: 15 pcs)",
      es: "Redy: stock bajo en Pepitos: • Tortillas: 12 pzas (umbral: 40 pzas) • Aguacates: 6 pzas (umbral: 15 pzas)",
    },
    time: "4:51 PM",
  },
];

export default function SmsHero() {
  const { t } = useLang();

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-[#FAFAF8]">
      <div
        className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 70%)" }}
      />
      <div
        className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(215,38,61,0.06) 0%, transparent 70%)" }}
      />

      <div className="relative max-w-[1280px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-redy-secondary hover:text-redy-charcoal transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#D7263D" }}
        >
          {t("SMS for customers + staff · Built in", "SMS para clientes + staff · Incorporado")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[40px] sm:text-[56px] md:text-[80px] lg:text-[96px] font-black leading-[0.98] tracking-[-0.04em] mb-6 text-redy-charcoal"
        >
          {t('"Your order', '"Tu orden')}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t('is ready." Sent.', 'está lista." Enviado.')}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-redy-secondary max-w-[680px] mx-auto leading-relaxed mb-12"
        >
          {t(
            "Order ready? SMS the customer. Schedule published? Every employee gets their shifts by text. Running low on stock? You get a text. Built into the POS, with opt-in and STOP handling built in.",
            "¿Orden lista? SMS al cliente. ¿Horario publicado? Cada empleado recibe sus turnos por SMS. ¿Se acaba el stock? Te llega un SMS. Incorporado al POS, con opt-in y manejo de STOP incluidos."
          )}
        </motion.p>

        {/* 3 iPhone Messages mockups */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-[1100px] mx-auto mb-12"
        >
          {CONVOS.map((convo, i) => (
            <MessagesPhone key={convo.label.en} convo={convo} index={i} />
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="text-center"
        >
          <Link
            href="/signup"
            className="group inline-flex items-center gap-2 text-[15px] font-bold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform shadow-2xl text-white"
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              boxShadow: "0 20px 60px rgba(244, 114, 43, 0.4)",
            }}
          >
            {t("Start texting your customers →", "Empieza a mensajear clientes →")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function MessagesPhone({ convo, index }: { convo: Convo; index: number }) {
  const { t } = useLang();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.6,
        delay: 0.7 + 0.15 * index,
        ease: [0.21, 0.47, 0.32, 0.98],
      }}
      className="flex flex-col items-center"
    >
      <div className="text-[10px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
        {t(convo.label.en, convo.label.es)}
      </div>

      {/* Phone frame */}
      <div className="w-[220px] h-[400px] bg-[#0a0a0a] rounded-[32px] p-[5px] shadow-[0_30px_70px_rgba(0,0,0,0.18)] relative">
        <div className="w-full h-full rounded-[27px] overflow-hidden bg-white relative flex flex-col">
          {/* Notch */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-3.5 bg-[#0a0a0a] rounded-full z-10" />

          {/* Status bar */}
          <div className="h-7 flex items-center justify-between px-4 text-[8px] font-bold text-redy-charcoal">
            <span>11:42</span>
            <span>5G</span>
          </div>

          {/* Conversation header */}
          <div className="px-3 py-2 border-b border-black/5 text-center">
            <div
              className="w-9 h-9 rounded-full mx-auto mb-1.5 flex items-center justify-center text-white font-black text-[12px]"
              style={{
                background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              }}
            >
              P
            </div>
            <div className="text-[10px] font-bold text-redy-charcoal">{convo.contact}</div>
            <div className="text-[8px] text-redy-secondary/60">{convo.status}</div>
          </div>

          {/* Bubbles */}
          <div className="flex-1 px-3 py-3 flex flex-col gap-2 justify-end">
            {/* Time */}
            <div className="text-center text-[7px] font-bold text-redy-secondary/55">
              {convo.time}
            </div>

            {/* Incoming bubble */}
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.4, delay: 1.0 + 0.15 * index }}
              className="rounded-[12px] rounded-bl-[3px] px-3 py-2 max-w-[85%] self-start"
              style={{ backgroundColor: "#F0EDE8" }}
            >
              <div className="text-[9px] text-redy-charcoal leading-snug">
                {t(convo.bubble.en, convo.bubble.es)}
              </div>
            </motion.div>

            {/* Reply bubble */}
            {convo.reply && (
              <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.4, delay: 1.5 + 0.15 * index }}
                className="rounded-[12px] rounded-br-[3px] px-3 py-1.5 self-end text-white"
                style={{
                  background: "linear-gradient(135deg, #007AFF 0%, #0051D5 100%)",
                  maxWidth: "70%",
                }}
              >
                <div className="text-[9px] leading-snug">
                  {t(convo.reply.en, convo.reply.es)}
                </div>
              </motion.div>
            )}

            {/* Delivered */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 1.8 + 0.15 * index }}
              className="text-right text-[7px] font-bold text-redy-secondary/55 pr-1"
            >
              {t("Delivered", "Entregado")}
            </motion.div>
          </div>

          {/* Compose bar */}
          <div className="px-3 py-2 border-t border-black/5 flex items-center gap-2">
            <div className="flex-1 bg-[#FAFAF8] rounded-full px-3 py-1 text-[8px] text-redy-secondary/50 italic">
              iMessage
            </div>
            <div className="w-5 h-5 rounded-full" style={{ background: "linear-gradient(135deg, #007AFF 0%, #0051D5 100%)" }} />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
